import { jsonSchema, streamText, type CoreMessage, type TextStreamPart, type ToolSet } from "ai"
import type { HarnessPart, HarnessRun } from "./types"

const MAX_STEPS = 12
const MAX_TOKENS = 16000
const NO_ARGUMENTS = { type: "object", properties: {} }
const CACHE = { anthropic: { cacheControl: { type: "ephemeral" } } }

function describe(error: unknown) {
  return error instanceof Error ? error.message : String(error)
}

// One turn: streams the model, runs the tools it calls and reports every part as it grows.
// A tool never throws into the loop: what it throws goes back to the model as { error }, so it can correct itself
export async function run({ model, system, context, history = [], message, tools = {}, maxSteps = MAX_STEPS, maxTokens = MAX_TOKENS, thinking, onParts }: HarnessRun) {
  const parts: HarnessPart[] = []

  const set: ToolSet = {}
  for (const [name, tool] of Object.entries(tools)) {
    set[name] = {
      description: tool.description,
      parameters: jsonSchema(tool.parameters ?? NO_ARGUMENTS),
      execute: async args => { try { return await tool.run(args) } catch (e) { return { error: describe(e) } } },
    }
  }

  const messages: CoreMessage[] = [{ role: "system", content: system, providerOptions: CACHE }]
  if (context) messages.push({ role: "system", content: context })
  messages.push(...history.map(m => ({ role: m.role, content: m.text })))
  messages.push({ role: "user", content: message })

  function grow(type: "text" | "thinking", delta: string) {
    const last = parts[parts.length - 1]
    if (last?.type === type) parts[parts.length - 1] = { type, text: last.text + delta }
    else parts.push({ type, text: delta })
  }

  try {
    const providerOptions = thinking ? { anthropic: { thinking: { type: "enabled", budgetTokens: thinking } } } : undefined
    const result = streamText({ model, messages, tools: set, maxSteps, maxTokens, providerOptions })
    // A ToolSet built at runtime types no result, so the parts are read untyped
    for await (const chunk of result.fullStream as AsyncIterable<TextStreamPart<any>>) {
      if (chunk.type === "text-delta") grow("text", chunk.textDelta)
      else if (chunk.type === "reasoning") grow("thinking", chunk.textDelta)
      else if (chunk.type === "tool-call") parts.push({ type: "tool", id: chunk.toolCallId, name: chunk.toolName, args: chunk.args, done: false })
      else if (chunk.type === "tool-result") {
        const index = parts.findIndex(p => p.type === "tool" && p.id === chunk.toolCallId)
        if (index >= 0) parts[index] = { ...parts[index], result: chunk.result, done: true } as HarnessPart
      }
      else if (chunk.type === "error") parts.push({ type: "error", text: describe(chunk.error) })
      else continue
      onParts?.([...parts])
    }
  } catch (e) {
    parts.push({ type: "error", text: describe(e) })
    onParts?.([...parts])
  }

  return parts
}
